import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Ban,
  CheckCircle2,
  Clock,
  History,
  MailWarning,
  Search,
  ShieldCheck,
  UserRound,
  X,
  XCircle,
} from "lucide-react";
import { useAdminRoles, useAdminUsers } from "../../hooks/useAdmin";
import { usePermissions, PERMISSIONS } from "../../hooks/usePermissions";
import { useFilterParams } from "../../hooks/useFilterParams";
import { ConfirmActionModal } from "./ConfirmActionModal";
import { ExpandableRow } from "./ExpandableRow";
import { Pagination } from "./Pagination";
import { readApiError } from "../../utils/readApiError";

const PAGE_SIZE = 25;

//: Chevron + the seven data columns; the inline detail row spans all of them.
const USER_COLUMN_COUNT = 8;

const KYC_FILTERS = [
  { label: "All", value: "" },
  { label: "Verified", value: "approved" },
  { label: "Pending", value: "pending" },
  { label: "Rejected", value: "rejected" },
];

const STATUS_FILTERS = [
  { label: "Any status", value: "" },
  { label: "Active", value: "active" },
  // Signed up but never completed the email OTP. Invisible before this filter
  // existed, which hid a conversion leak: these are people who tried to join
  // and got stuck, not people anyone disabled.
  { label: "Awaiting verification", value: "pending_verification" },
  { label: "Suspended", value: "suspended" },
  { label: "Closed", value: "closed" },
];

const FILTER_DEFAULTS = {
  search: "",
  kyc_status: "",
  account_status: "",
  offset: 0,
  // Which row is open. In the URL so a refresh or a shared link reopens the
  // same record rather than collapsing back to a bare list.
  expanded: "",
};

export function AdminUsers() {
  // Filters live in the URL so a view can be shared, bookmarked, and survive a
  // refresh, and so the Overview cards can deep-link into a specific queue.
  const { values, setValues, setFilters, clearFilters, isFiltered } =
    useFilterParams(FILTER_DEFAULTS);
  const {
    search,
    kyc_status: kycStatus,
    account_status: accountStatus,
    offset,
    expanded,
  } = values;

  const [searchInput, setSearchInput] = useState(search);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionError, setActionError] = useState(null);

  const { can } = usePermissions();
  const canWrite = can(PERMISSIONS.USERS_WRITE);
  const canChangeRole = can(PERMISSIONS.USERS_ROLE_WRITE);
  const canReadAudit = can(PERMISSIONS.AUDIT_READ);
  const { roles } = useAdminRoles({ enabled: canChangeRole });

  // Keep the box in step when the URL changes from outside this input - a
  // deep link from the dashboard, the back button, or Clear filters.
  useEffect(() => {
    setSearchInput((current) => (current === search ? current : search));
  }, [search]);

  // Debounce so each keystroke does not hit the API. `replace` keeps typing
  // out of browser history: back should undo the search, not each character.
  useEffect(() => {
    const typed = searchInput.trim();
    if (typed === search) return undefined;
    const timer = setTimeout(
      () => setValues({ search: typed, offset: 0 }, { replace: true }),
      300
    );
    return () => clearTimeout(timer);
  }, [searchInput, search, setValues]);

  const params = useMemo(
    () => ({ limit: PAGE_SIZE, offset, search, kycStatus, accountStatus }),
    [offset, search, kycStatus, accountStatus]
  );

  const {
    users,
    total,
    isLoading,
    error,
    updateAccountStatus,
    isUpdatingAccountStatus,
    updateUserRole,
    isUpdatingRole,
  } = useAdminUsers(params);

  // One row open at a time: the detail is tall, and two open rows push the
  // rest of the queue off screen, which is the problem this was meant to fix.
  function toggleUser(userId) {
    setActionError(null);
    setValues({ expanded: expanded === userId ? "" : userId });
  }

  async function confirmPendingAction(reason) {
    if (!pendingAction) return;
    setActionError(null);
    try {
      if (pendingAction.kind === "account-status") {
        await updateAccountStatus({
          userId: pendingAction.user.id,
          status: pendingAction.status,
          reason,
        });
      } else if (pendingAction.kind === "role") {
        await updateUserRole({
          userId: pendingAction.user.id,
          role: pendingAction.role,
          reason,
        });
      }
      // The row re-renders from the refetched list, so there is no local copy
      // of the user to keep in sync.
      setPendingAction(null);
    } catch (err) {
      setActionError(readApiError(err, "Could not complete that action."));
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40 mb-1">
            User Management
          </h2>
          <h1 className="text-2xl md:text-3xl text-[#0A4730] font-serif font-semibold">
            Users &amp; KYC Verification
          </h1>
        </div>
        <div className="flex items-center gap-1 bg-white p-1 rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
          {KYC_FILTERS.map((filter) => (
            <button
              key={filter.value || "all"}
              onClick={() => setFilters({ kyc_status: filter.value })}
              className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                kycStatus === filter.value
                  ? "bg-[#0A4730] text-white shadow-sm"
                  : "text-[#0A2540]/70 hover:bg-[#F5F9F4]"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-sm text-red-600">
          Could not load users from the admin API.
        </div>
      )}

      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-6">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <input
              aria-label="Search users"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by name, email, phone, or user ID…"
              className="w-full rounded-xl bg-white border border-slate-200 pl-11 pr-4 py-3 text-sm text-[#0A2540] focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5"
            />
          </div>
          <select
            aria-label="Filter by account status"
            value={accountStatus}
            onChange={(event) => setFilters({ account_status: event.target.value })}
            className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-[#0A4730]"
          >
            {STATUS_FILTERS.map((option) => (
              <option key={option.value || "any"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {isFiltered && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs font-semibold text-[#0A4730] hover:bg-slate-50 transition"
            >
              <X size={14} /> Clear filters
            </button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-100">
                <Header />
                <Header>User Details</Header>
                <Header>Country</Header>
                <Header>Role</Header>
                <Header>Account</Header>
                <Header>KYC</Header>
                <Header>Joined</Header>
                <Header align="right">Review</Header>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <EmptyRow text="Loading users…" />
              ) : users.length === 0 ? (
                <EmptyRow
                  text={
                    isFiltered
                      ? "No users match these filters."
                      : "No users have signed up yet."
                  }
                  action={
                    isFiltered ? (
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="mt-2 text-xs font-semibold text-[#0A4730] underline underline-offset-4"
                      >
                        Clear filters
                      </button>
                    ) : null
                  }
                />
              ) : (
                users.map((user) => (
                  <ExpandableRow
                    key={user.id}
                    columnCount={USER_COLUMN_COUNT}
                    label={user.full_name || user.email || "Unnamed user"}
                    expanded={expanded === user.id}
                    onToggle={() => toggleUser(user.id)}
                    detail={
                      <UserDetail
                        user={user}
                        canWrite={canWrite}
                        canChangeRole={canChangeRole}
                        canReadAudit={canReadAudit}
                        roles={roles}
                        actionError={actionError}
                        pendingAction={pendingAction}
                        setPendingAction={setPendingAction}
                        onClose={() => toggleUser(user.id)}
                      />
                    }
                  >
                    <td className="py-4 pr-4">
                      <div className="font-medium text-[#0A2540]">
                        {user.full_name || "Unnamed user"}
                      </div>
                      <div className="text-xs text-slate-400">
                        {user.email || user.phone_number}
                      </div>
                    </td>
                    <td className="py-4 pr-4 text-sm text-[#0A2540]/80">
                      {[user.country_code, user.default_currency]
                        .filter(Boolean)
                        .join(" / ") || "—"}
                    </td>
                    <td className="py-4 pr-4 text-xs font-semibold text-slate-500 capitalize">
                      {user.role}
                    </td>
                    <td className="py-4 pr-4">
                      <AccountStatusBadge status={user.account_status} />
                    </td>
                    <td className="py-4 pr-4">
                      <KycBadge status={user.kyc_status} />
                    </td>
                    <td className="py-4 pr-4 text-sm text-slate-500">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="py-4 text-right">
                      <span className="text-xs font-semibold text-[#0A4730]/70">
                        {expanded === user.id ? "Hide" : "Review"}
                      </span>
                    </td>
                  </ExpandableRow>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          total={total}
          limit={PAGE_SIZE}
          offset={offset}
          onChange={(next) => setValues({ offset: next })}
          isLoading={isLoading}
        />
      </div>


      <ConfirmActionModal
        open={Boolean(pendingAction)}
        title={pendingAction?.title || "Confirm action"}
        description={pendingAction?.description}
        confirmLabel={pendingAction?.label || "Confirm"}
        destructive={pendingAction?.destructive}
        // Reactivation is the only change that needs no justification.
        requireReason={pendingAction?.status !== "active"}
        error={actionError}
        isSubmitting={isUpdatingAccountStatus || isUpdatingRole}
        onConfirm={confirmPendingAction}
        onCancel={() => {
          setPendingAction(null);
          setActionError(null);
        }}
      />
    </div>
  );
}

/**
 * Build the confirmation for a role change, naming what the new role actually
 * grants. "Change role to operations?" is not enough information to consent
 * to; granting the ability to move money should say so.
 */
function roleAction(user, role, roles) {
  const target = roles.find((entry) => entry.role === role);
  const staffNote = target?.is_staff
    ? `Grants ${target.permissions.length} admin permission(s), including: ${target.permissions
        .slice(0, 4)
        .join(", ")}${target.permissions.length > 4 ? "…" : ""}.`
    : "Removes all admin access. This person will only be able to use the customer app.";

  return {
    kind: "role",
    user,
    role,
    label: "Change role",
    title: `Change role from ${user.role} to ${role}?`,
    description: `${staffNote} The change takes effect immediately.`,
    // Granting staff access is the privileged direction and deserves the
    // red treatment; demoting to customer only ever removes power.
    destructive: Boolean(target?.is_staff),
  };
}

/** Which lifecycle transitions are offered from the current status. */
function accountStatusActions(current) {
  switch (current) {
    case "pending_verification":
      return [
        {
          status: "active",
          label: "Activate manually",
          title: "Activate without email verification?",
          description:
            "Use only when identity is confirmed another way. This bypasses the email verification this account never completed.",
          destructive: false,
        },
        {
          status: "closed",
          label: "Close account",
          title: "Close this unverified account?",
          description:
            "Use for abandoned or fraudulent signups. Closure is terminal; the record is retained.",
          destructive: true,
        },
      ];
    case "active":
      return [
        {
          status: "suspended",
          label: "Suspend account",
          title: "Suspend this account?",
          description:
            "The customer will be signed out and unable to send money until reactivated.",
          destructive: true,
        },
        {
          status: "closed",
          label: "Close account",
          title: "Close this account?",
          description:
            "Closure is a terminal state. Financial history is retained for regulatory record-keeping; accounts are never hard-deleted.",
          destructive: true,
        },
      ];
    case "suspended":
      return [
        {
          status: "active",
          label: "Reactivate account",
          title: "Reactivate this account?",
          description: "The customer will be able to sign in and transact again.",
          destructive: false,
        },
        {
          status: "closed",
          label: "Close account",
          title: "Close this account?",
          description: "Closure is a terminal state.",
          destructive: true,
        },
      ];
    case "closed":
      return [
        {
          status: "active",
          label: "Reopen account",
          title: "Reopen this closed account?",
          description: "The customer regains access to sign in and transact.",
          destructive: false,
        },
      ];
    default:
      return [];
  }
}


function Header({ children = null, align = "left" }) {
  return (
    <th
      className={`pb-4 text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40 ${
        align === "right" ? "text-right" : ""
      }`}
    >
      {children}
    </th>
  );
}

function EmptyRow({ text, action = null }) {
  return (
    <tr>
      <td colSpan={USER_COLUMN_COUNT} className="py-10 text-center text-sm text-[#0A2540]/50">
        {text}
        {action}
      </td>
    </tr>
  );
}

/**
 * `capitalize` is applied only where it is safe. It used to be on every field,
 * which rendered "ada@example.com" as "Ada@example.com" and mangled phone
 * numbers and ids - values a support agent reads back to a customer or pastes
 * into a provider console, so they have to be exact.
 */
function Detail({ label, value, capitalize = false }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-widest text-slate-400 mb-1">{label}</p>
      <p className={`text-[#0A2540] break-all ${capitalize ? "capitalize" : ""}`}>
        {value}
      </p>
    </div>
  );
}

/**
 * The fallback used to read `humanize(status || "active")`, so an account with
 * a missing or unrecognised status displayed as **Active** - the console
 * asserting someone could transact when it had no idea. An unknown state is
 * now shown as unknown.
 */
function AccountStatusBadge({ status }) {
  const config = {
    active: { style: "bg-emerald-50 text-emerald-600 border-emerald-100", icon: ShieldCheck },
    pending_verification: {
      style: "bg-sky-50 text-sky-600 border-sky-100",
      icon: MailWarning,
      label: "Awaiting verification",
    },
    suspended: { style: "bg-amber-50 text-amber-600 border-amber-100", icon: Ban },
    closed: { style: "bg-slate-100 text-slate-600 border-slate-200", icon: XCircle },
  }[status];

  const style = config?.style ?? "bg-slate-50 text-slate-500 border-slate-200";
  const Icon = config?.icon ?? Clock;
  const label = config?.label ?? (status ? humanize(status) : "Unknown");

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border capitalize ${style}`}
    >
      <Icon size={14} />
      {label}
    </span>
  );
}

function KycBadge({ status }) {
  const config = {
    approved: { style: "bg-emerald-50 text-emerald-600 border-emerald-100", icon: CheckCircle2 },
    pending: { style: "bg-amber-50 text-amber-600 border-amber-100", icon: Clock },
    rejected: { style: "bg-red-50 text-red-500 border-red-100", icon: XCircle },
  }[status] || { style: "bg-slate-50 text-slate-600 border-slate-100", icon: Clock };
  const Icon = config.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border capitalize ${config.style}`}
    >
      <Icon size={14} />
      {humanize(status)}
    </span>
  );
}

function humanize(value = "") {
  return value.replaceAll("_", " ");
}

function formatDate(value) {
  return value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "Never";
}



/**
 * The expanded detail for one user row.
 *
 * Rendered inline beneath the row rather than below the whole table, so a
 * reviewer keeps their place in the list.
 */
function UserDetail({
  user: selectedUser,
  canWrite,
  canChangeRole,
  canReadAudit,
  roles,
  actionError,
  pendingAction,
  setPendingAction,
  onClose,
}) {
  return (
    <div className="text-left space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-[#C0F6B7]/40 text-[#0A4730] grid place-items-center">
                <UserRound size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-[#0A2540]">
                  {selectedUser.full_name || "Unnamed user"}
                </h3>
                <p className="text-xs text-slate-400 font-mono">{selectedUser.id}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Answering "what has been done to this account, by whom?"
                  previously meant leaving for the Audit page and retyping the
                  id. The trail is only useful if it is one click away from the
                  record it describes. */}
              {canReadAudit && (
                <Link
                  to={`/admin/audit?target_type=user&target_id=${selectedUser.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0A4730] hover:underline underline-offset-4"
                >
                  <History size={14} /> Audit history
                </Link>
              )}
              <button
                onClick={onClose}
                className="text-xs font-semibold text-slate-400 hover:text-[#0A4730]"
              >
                Close
              </button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <Detail label="Email" value={selectedUser.email || "—"} />
            <Detail label="Phone" value={selectedUser.phone_number} />
            <Detail
              label="Compliance"
              value={humanize(selectedUser.compliance_status)}
              capitalize
            />
            <Detail label="Last login" value={formatDate(selectedUser.last_login_at)} />
          </div>

          {selectedUser.account_status === "pending_verification" && (
            <div className="rounded-2xl bg-sky-50 border border-sky-100 p-4 flex gap-3">
              <MailWarning size={18} className="text-sky-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-sky-900 font-medium">
                  Signup not finished
                </p>
                <p className="text-sm text-sky-800/80 mt-0.5">
                  This person registered but never entered their email
                  verification code, so they cannot sign in yet. Nobody disabled
                  this account.
                </p>
              </div>
            </div>
          )}

          {selectedUser.account_status_reason && (
            <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4">
              <p className="text-[9px] uppercase tracking-widest text-amber-700 font-semibold mb-1">
                Current status reason
              </p>
              <p className="text-sm text-amber-800">
                {selectedUser.account_status_reason}
              </p>
              {selectedUser.account_status_changed_at && (
                <p className="text-xs text-amber-700/70 mt-1">
                  Set {formatDate(selectedUser.account_status_changed_at)}
                </p>
              )}
            </div>
          )}

          {/* While a confirmation is open the error belongs in the dialog, so
              it is not repeated here. */}
          {actionError && !pendingAction && (
            <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-sm text-red-600">
              {actionError}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            {canWrite &&
              accountStatusActions(selectedUser.account_status).map((action) => (
                <button
                  key={action.status}
                  onClick={() =>
                    setPendingAction({
                      kind: "account-status",
                      user: selectedUser,
                      status: action.status,
                      ...action,
                    })
                  }
                  className={`px-4 py-2.5 rounded-xl text-xs font-semibold text-white ${
                    action.destructive ? "bg-red-500" : "bg-[#0A4730]"
                  }`}
                >
                  {action.label}
                </button>
              ))}

            {canChangeRole && roles.length > 0 && (
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-[#0A2540]/70">
                Role
                <select
                  aria-label="Change role"
                  value={selectedUser.role}
                  onChange={(event) =>
                    setPendingAction(roleAction(selectedUser, event.target.value, roles))
                  }
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-[#0A4730] capitalize"
                >
                  {roles.map((entry) => (
                    <option key={entry.role} value={entry.role}>
                      {entry.role}
                      {entry.is_staff ? ` (staff, ${entry.permissions.length})` : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {!canWrite && !canChangeRole && (
              <p className="text-xs text-slate-400">
                You have read-only access to user accounts.
              </p>
            )}
          </div>
    </div>
  );
}